import React, { FC, useEffect, useCallback, useMemo, useState } from 'react';
import {
  ShopifyLocalCheckout,
  ShopifyLocalCheckoutLineItem,
} from '../../types/api/entities/shopify';
import { connect } from 'react-redux';
import { StoreState } from '../../store/state';
import { Dispatch } from 'redux';
import { getRecipeProducts } from '../../store/actions/api/get-recipe-products';
import { addCheckoutLineItems } from '../../store/actions/shopify/add-checkout-line-items';
import { ProductCard } from './product-card';
import { RelatedProduct } from '../../types/api/entities/recipe';
import { RecipeProductWithShopifyVariant } from '../../store/state/shopify';
import { withTranslation, WithTranslation } from 'next-i18next';
import { Store } from '../../types/api/entities/store';
import { FaCheck } from 'react-icons/fa';
import { setCartIsOpen } from '../../store/actions/shopify/set-cart-is-open';

export interface OwnProps {
  recipeId: number;
}

export interface StateProps {
  store: Store;
  recipeProducts: RelatedProduct[];
  products: RecipeProductWithShopifyVariant[];
  checkout: ShopifyLocalCheckout;
}

export interface DispatchProps {
  getRecipeProducts(recipeId: number, products: RelatedProduct[]): void;
  addCheckoutLineItems(recipeId: number, lineItems: ShopifyLocalCheckoutLineItem[]): void;
  setCartIsOpen(value: boolean): void;
}

export type RecipeProductsProps = OwnProps & StateProps & DispatchProps;

const _RecipeProducts: FC<RecipeProductsProps & WithTranslation> = ({
  store,
  recipeId,
  products,
  recipeProducts,
  getRecipeProducts,
  addCheckoutLineItems,
  checkout,
  setCartIsOpen,
  t,
}) => {
  const [productsRequestDone, setProductsRequestDone] = useState(false);

  useEffect(() => {
    if (productsRequestDone) return;

    if (typeof window !== 'undefined' && recipeProducts.length > 0 && products.length === 0) {
      getRecipeProducts(recipeId, recipeProducts);
      setProductsRequestDone(true);
    }
  }, [recipeId, recipeProducts, productsRequestDone, getRecipeProducts, products.length]);

  const addProductsToCheckout = useCallback(
    () =>
      addCheckoutLineItems(
        recipeId,
        products.map((product) => ({
          quantity: product.recipeProduct.quantity,
          variant: product.shopifyVariant,
        })),
      ),
    [recipeId, products, addCheckoutLineItems],
  );

  const openCart = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setCartIsOpen(true);
  }, [setCartIsOpen]);

  const primaryColor = store.theme?.primaryColor ?? '#78a12f';

  const buttonState = useMemo(() => {
    const productsAreUnavailable = products.every(
      (product) => !product.shopifyVariant.availableForSale,
    );

    if (productsAreUnavailable) {
      return {
        content: t('OutOfStock'),
        disabled: true,
        backgroundColor: '#acb4b9',
        onClick: undefined,
      };
    }

    const productsAreInCart =
      checkout.recipeIds.includes(recipeId) &&
      products.every((product) =>
        checkout.lineItems.some((lineItem) => lineItem.variant.id === product.shopifyVariant.id),
      );

    if (productsAreInCart) {
      return {
        content: <FaCheck />,
        disabled: false,
        backgroundColor: primaryColor,
        onClick: openCart,
      };
    }

    return {
      content: t('AddToCart'),
      disabled: false,
      backgroundColor: primaryColor,
      onClick: addProductsToCheckout,
    };
  }, [products, checkout, openCart, addProductsToCheckout, t, primaryColor, recipeId]);

  if (!recipeProducts.length || !products.length) {
    return null;
  }

  return (
    <div>
      {products.map((product, index) => {
        return (
          <ProductCard
            key={product.recipeProduct.id}
            product={product}
            style={{ marginTop: index === 0 ? undefined : '8px' }}
          />
        );
      })}

      <button
        style={{
          height: '45px',
          borderRadius: '4px',
          border: 'none',
          color: '#fbfbfb',
          padding: '0 18px',
          fontFamily: 'var(--font-family-primary)',
          fontSize: '15px',
          width: '100%',
          marginTop: '8px',
          backgroundColor: buttonState.backgroundColor,
          cursor: buttonState.disabled ? undefined : 'pointer',
        }}
        disabled={buttonState.disabled}
        onClick={buttonState.onClick}
      >
        {buttonState.content}
      </button>
    </div>
  );
};

function mapStateToProps(store: StoreState, props: OwnProps): StateProps {
  const { recipe } = store.entities;
  const { productsByRecipeId } = store.shopify;

  const result: StateProps = {
    store: store.entities.store,
    products: productsByRecipeId[props.recipeId] || [],
    recipeProducts: recipe.byId[props.recipeId]?.products ?? [],
    checkout: store.shopify.checkout,
  };

  return result;
}

function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    getRecipeProducts: (recipeId: number, products: RelatedProduct[]) =>
      dispatch(getRecipeProducts(recipeId, products)),
    addCheckoutLineItems: (recipeId: number, lineItems: ShopifyLocalCheckoutLineItem[]) =>
      dispatch(addCheckoutLineItems(recipeId, lineItems)),
    setCartIsOpen: (cartIsOpen) => dispatch(setCartIsOpen(cartIsOpen)),
  };
}

export const RecipeProducts = withTranslation()(
  connect(mapStateToProps, mapDispatchToProps)(_RecipeProducts),
);
